import { Link, useLocation, matchPath } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import {
  Box,
  Button,
  SideNavLink,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@razorpay/blade/components';
import { useAppSelector } from '@store/index';
import { selectTenantConfig } from '@store/slices/clientSlice';
import TenantLogo from '@components/TenantLogo';
import { MerchantSelector } from '@components/MerchantSelector';
import { useSideNav } from '@src/context/SideNavContext';
import { SIDENAV_WIDTHS } from '@constants/sidenavConfig';
import { getNavLinks, type NavLinkItem } from '@utils/navLinks';

const isLinkActive = (pathname: string, href: string): boolean => {
  return Boolean(matchPath({ path: href, end: href === '/' }, pathname));
};

const NavLink = ({ href, title }: NavLinkItem) => {
  const { pathname } = useLocation();
  const isActive = isLinkActive(pathname, href);

  return (
    <SideNavLink href={href} title={title} as={Link} isActive={isActive} />
  );
};

export default function AppSideNav() {
  const [cookies] = useCookies(['role']);
  const role = cookies.role ?? '';
  const tenant = useAppSelector(selectTenantConfig);
  const { isCollapsed, toggleCollapse } = useSideNav();

  const links = getNavLinks(role);
  const sidebarWidth = isCollapsed
    ? SIDENAV_WIDTHS.collapsed
    : SIDENAV_WIDTHS.expanded;

  return (
    <Box
      position="fixed"
      left="spacing.0"
      top="spacing.0"
      height="100vh"
      width={sidebarWidth}
      backgroundColor="surface.background.gray.subtle"
      display="flex"
      flexDirection="column"
      overflowY="hidden"
      zIndex="2"
      borderRightWidth="thicker"
      transition="width 0.3s ease"
    >
      {/* Tenant logo */}
      <Box
        padding="spacing.5"
        paddingBottom="spacing.4"
        display="flex"
        alignItems="center"
        justifyContent={isCollapsed ? 'center' : 'flex-start'}
        flexShrink="0"
      >
        <TenantLogo
          tenant={tenant}
          className={isCollapsed ? 'h-[40px] w-auto' : 'h-[48px] w-auto'}
        />
      </Box>

      {/* Collapse/Expand toggle */}
      <Box
        display="flex"
        padding="spacing.3"
        paddingX="spacing.4"
        justifyContent={isCollapsed ? 'center' : 'flex-end'}
        flexShrink="0"
      >
        <Button
          variant="tertiary"
          color="primary"
          icon={isCollapsed ? ChevronRightIcon : ChevronLeftIcon}
          onClick={toggleCollapse}
          accessibilityLabel={
            isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
          }
          size="small"
        />
      </Box>

      {/* Navigation links */}
      {!isCollapsed && (
        <Box
          paddingX="spacing.4"
          paddingY="spacing.2"
          overflowY="auto"
          flex="1"
        >
          {links.map((link) => (
            <NavLink key={link.href} {...link} />
          ))}
        </Box>
      )}

      {/* Merchant selector */}
      {!isCollapsed && (
        <Box paddingX="spacing.4" paddingY="spacing.5" flexShrink="0">
          <MerchantSelector />
        </Box>
      )}
    </Box>
  );
}
